import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, Heart, Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 5: TARA — Editorial Chic
 * Style: Magazine-editorial, sophisticated, fashion-forward
 * Colors: Charcoal (#2C2C2C) + Warm Cream (#FAF9F6)
 * Fonts: Cormorant Garamond (headings) + Outfit (body)
 */
const Template5 = () => {
    const navigate = useNavigate();
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [wishlist, setWishlist] = useState({});
    const [email, setEmail] = useState('');
    const touchStartX = useRef(0);

    // Editor's Picks
    const editorsPicks = [
        {
            name: 'The Bridal Edit',
            description: 'Timeless silhouettes reimagined with modern Indian craftsmanship for your most special day.',
            image: 'https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=800',
            link: '/template5/shop'
        },
        {
            name: 'Festive Couture',
            description: 'Celebrate in style with handcrafted ensembles that honor tradition while embracing contemporary elegance.',
            image: 'https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=800',
            link: '/template5/shop'
        },
        {
            name: 'Statement Drapes',
            description: 'Where six yards of fabric become a canvas for artistic expression and personal style.',
            image: 'https://images.pexels.com/photos/2220316/pexels-photo-2220316.jpeg?auto=compress&cs=tinysrgb&w=800',
            link: '/template5/shop'
        }
    ];

    // Categories
    const categories = [
        { name: 'Sarees', image: 'https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=600', count: '48 Pieces' },
        { name: 'Lehengas', image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=600', count: '36 Pieces' },
        { name: 'Kurtas', image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=600', count: '52 Pieces' },
        { name: 'Indo-Western', image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=600', count: '28 Pieces' }
    ];

    // Products
    const products = [
        { id: 1, name: 'Chanderi Silk Saree', price: '₹12,999', category: 'Sarees', image: 'https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 2, name: 'Embroidered Lehenga', price: '₹34,999', category: 'Lehengas', image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 3, name: 'Banarasi Silk Kurta', price: '₹8,499', category: 'Kurtas', image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 4, name: 'Designer Anarkali', price: '₹18,999', category: 'Indo-Western', image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 5, name: 'Kanjivaram Drape', price: '₹24,999', category: 'Sarees', image: 'https://images.pexels.com/photos/4614070/pexels-photo-4614070.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 6, name: 'Zardozi Lehenga', price: '₹29,999', category: 'Lehengas', image: 'https://images.pexels.com/photos/6712360/pexels-photo-6712360.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 7, name: 'Organza Cape Set', price: '₹14,999', category: 'Indo-Western', image: 'https://images.pexels.com/photos/7275385/pexels-photo-7275385.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 8, name: 'Heritage Silk Saree', price: '₹4,999', category: 'Sarees', image: 'https://images.pexels.com/photos/8953474/pexels-photo-8953474.jpeg?auto=compress&cs=tinysrgb&w=400' }
    ];

    // Lookbook
    const lookbookImages = [
        'https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=600',
        'https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=600',
        'https://images.pexels.com/photos/2220316/pexels-photo-2220316.jpeg?auto=compress&cs=tinysrgb&w=600'
    ];

    // Testimonials
    const testimonials = [
        {
            text: 'TARA\'s editorial approach to Indian fashion is refreshing. The Chanderi saree I purchased drapes like poetry. Every detail speaks of thoughtful craftsmanship.',
            name: 'Ananya Sharma',
            city: 'Mumbai',
            rating: 5
        },
        {
            text: 'The lehenga from their festive collection was the highlight of our celebration. The embroidery work is museum-worthy. TARA understands Indian women.',
            name: 'Meera Patel',
            city: 'Ahmedabad',
            rating: 5
        },
        {
            text: 'I discovered TARA through a fashion editorial and fell in love with their Indo-Western pieces. The quality matches the vision — sophisticated yet rooted.',
            name: 'Priya Iyer',
            city: 'Chennai',
            rating: 5
        }
    ];

    // Auto-rotate testimonials
    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    // Touch swipe for testimonials
    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
            setActiveTestimonial((prev) => diff > 0 ? (prev + 1) % testimonials.length : (prev - 1 + testimonials.length) % testimonials.length);
        }
    };

    const toggleWishlist = (e, id) => { e.stopPropagation(); setWishlist((prev) => ({ ...prev, [id]: !prev[id] })); };

    return (
        <div style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }} className="w-full min-h-screen">
            {/* Google Fonts */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=Outfit:wght@300;400;500;600&display=swap');
            `}</style>

            {/* ===== SECTION 1: MAGAZINE GRID HERO ===== */}
            <section className="w-full" style={{ backgroundColor: '#FAF9F6' }}>
                {/* Mobile Hero */}
                <div className="md:hidden">
                    <div className="relative w-full h-[85vh] overflow-hidden">
                        <img
                            src="https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=1200"
                            alt="Indian Fashion Editorial"
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-[#1A1A1A]/40 to-transparent" />
                        <div className="relative h-full flex flex-col justify-end pb-10 px-5">
                            <p
                                className="text-[11px] tracking-[0.3em] uppercase mb-3 text-[#FAF9F6]/70"
                                style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}
                            >
                                Spring / Summer 2026
                            </p>
                            <h1
                                className="text-4xl leading-[1.1] mb-3 text-[#FAF9F6]"
                                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
                            >
                                The Art of<br />Indian Fashion
                            </h1>
                            <p
                                className="text-sm text-[#FAF9F6]/60 mb-6 max-w-[280px] font-light leading-relaxed"
                                style={{ fontFamily: "'Outfit', sans-serif" }}
                            >
                                Where heritage meets editorial elegance in every thread and silhouette.
                            </p>
                            <button
                                onClick={() => navigate('/template5/shop')}
                                className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-[#FAF9F6] border-b border-[#FAF9F6]/40 pb-1 self-start hover:border-[#FAF9F6] transition-colors"
                                style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400 }}
                            >
                                Explore Collection <ArrowRight className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Desktop Hero - Magazine Grid */}
                <div className="hidden md:block">
                    <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-8 lg:py-12">
                        <div className="grid grid-cols-2 gap-4 lg:gap-6 h-[75vh] lg:h-[80vh] max-h-[700px]">
                            {/* Large Image Left */}
                            <div className="relative overflow-hidden group cursor-pointer" onClick={() => navigate('/template5/shop')}>
                                <img
                                    src="https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=1200"
                                    alt="Designer Saree Collection"
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/70 via-transparent to-transparent" />
                                {/* Editorial Headline Overlay */}
                                <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-10">
                                    <p
                                        className="text-[11px] lg:text-xs tracking-[0.3em] uppercase mb-3 text-[#FAF9F6]/70"
                                        style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}
                                    >
                                        Spring / Summer 2026
                                    </p>
                                    <h1
                                        className="text-3xl lg:text-5xl xl:text-6xl leading-[1.08] mb-3 text-[#FAF9F6]"
                                        style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
                                    >
                                        The Art of<br />Indian Fashion
                                    </h1>
                                    <p
                                        className="text-sm lg:text-base text-[#FAF9F6]/60 mb-5 max-w-[380px] font-light leading-relaxed"
                                        style={{ fontFamily: "'Outfit', sans-serif" }}
                                    >
                                        Where heritage meets editorial elegance in every thread and silhouette.
                                    </p>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); navigate('/template5/shop'); }}
                                        className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#FAF9F6] border-b border-[#FAF9F6]/40 pb-1 hover:border-[#FAF9F6] transition-colors"
                                        style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400 }}
                                    >
                                        Explore Collection <ArrowRight className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            {/* Right Column - 2 Stacked Images */}
                            <div className="grid grid-rows-2 gap-4 lg:gap-6">
                                <div className="relative overflow-hidden group cursor-pointer" onClick={() => navigate('/template5/shop')}>
                                    <img
                                        src="https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=600"
                                        alt="Couture Lehengas"
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/60 to-transparent" />
                                    <div className="absolute bottom-0 left-0 right-0 p-5 lg:p-6">
                                        <p
                                            className="text-[10px] tracking-[0.25em] uppercase text-[#FAF9F6]/60 mb-1"
                                            style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}
                                        >
                                            New Arrivals
                                        </p>
                                        <h3
                                            className="text-xl lg:text-2xl text-[#FAF9F6]"
                                            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                                        >
                                            Couture Lehengas
                                        </h3>
                                    </div>
                                </div>
                                <div className="relative overflow-hidden group cursor-pointer" onClick={() => navigate('/template5/shop')}>
                                    <img
                                        src="https://images.pexels.com/photos/2220316/pexels-photo-2220316.jpeg?auto=compress&cs=tinysrgb&w=600"
                                        alt="Statement Pieces"
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/60 to-transparent" />
                                    <div className="absolute bottom-0 left-0 right-0 p-5 lg:p-6">
                                        <p
                                            className="text-[10px] tracking-[0.25em] uppercase text-[#FAF9F6]/60 mb-1"
                                            style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}
                                        >
                                            Trending Now
                                        </p>
                                        <h3
                                            className="text-xl lg:text-2xl text-[#FAF9F6]"
                                            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                                        >
                                            Statement Pieces
                                        </h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 2: EDITOR'S PICKS ===== */}
            <section className="py-14 md:py-20 lg:py-28" style={{ backgroundColor: '#FAF9F6' }}>
                <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
                    {/* Section Header */}
                    <div className="text-center mb-10 md:mb-14">
                        <p
                            className="text-[11px] md:text-xs tracking-[0.3em] uppercase text-[#71717A] mb-3"
                            style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}
                        >
                            Curated for You
                        </p>
                        <h2
                            className="text-3xl md:text-4xl lg:text-5xl text-[#1A1A1A]"
                            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
                        >
                            Editor's Picks
                        </h2>
                    </div>

                    {/* Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 lg:gap-8">
                        {editorsPicks.map((pick, index) => (
                            <div key={index} className="group cursor-pointer" onClick={() => navigate(pick.link)}>
                                <div className="relative overflow-hidden aspect-[3/4] mb-4 md:mb-5">
                                    <img
                                        src={pick.image}
                                        alt={pick.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                                    />
                                    <div className="absolute inset-0 bg-[#1A1A1A]/0 group-hover:bg-[#1A1A1A]/10 transition-colors duration-500" />
                                </div>
                                <h3
                                    className="text-xl md:text-2xl text-[#1A1A1A] mb-2"
                                    style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                                >
                                    {pick.name}
                                </h3>
                                <p
                                    className="text-sm text-[#71717A] font-light leading-relaxed mb-3"
                                    style={{ fontFamily: "'Outfit', sans-serif" }}
                                >
                                    {pick.description}
                                </p>
                                <span
                                    className="inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase text-[#2C2C2C] border-b border-[#2C2C2C]/30 pb-0.5 group-hover:border-[#2C2C2C] transition-colors"
                                    style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400 }}
                                >
                                    Discover <ArrowRight className="w-3.5 h-3.5" />
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 3: CATEGORIES ===== */}
            <section className="py-14 md:py-20 lg:py-28" style={{ backgroundColor: '#F5F4F0' }}>
                <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
                    <div className="text-center mb-10 md:mb-14">
                        <p
                            className="text-[11px] md:text-xs tracking-[0.3em] uppercase text-[#71717A] mb-3"
                            style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}
                        >
                            Browse by
                        </p>
                        <h2
                            className="text-3xl md:text-4xl lg:text-5xl text-[#1A1A1A]"
                            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
                        >
                            Collections
                        </h2>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                        {categories.map((cat, index) => (
                            <div
                                key={index}
                                className="group cursor-pointer"
                                onClick={() => navigate('/template5/shop')}
                            >
                                <div className="relative overflow-hidden aspect-[3/4] mb-3 md:mb-4">
                                    <img
                                        src={cat.image}
                                        alt={cat.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/50 to-transparent" />
                                    <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                                        <h3
                                            className="text-lg md:text-xl text-[#FAF9F6] mb-0.5"
                                            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                                        >
                                            {cat.name}
                                        </h3>
                                        <p
                                            className="text-[10px] md:text-[11px] tracking-[0.15em] uppercase text-[#FAF9F6]/60"
                                            style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}
                                        >
                                            {cat.count}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 4: PRODUCTS ===== */}
            <section className="py-14 md:py-20 lg:py-28" style={{ backgroundColor: '#FAF9F6' }}>
                <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 md:mb-14 gap-4">
                        <div>
                            <p
                                className="text-[11px] md:text-xs tracking-[0.3em] uppercase text-[#71717A] mb-3"
                                style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}
                            >
                                Our Curation
                            </p>
                            <h2
                                className="text-3xl md:text-4xl lg:text-5xl text-[#1A1A1A]"
                                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
                            >
                                New Arrivals
                            </h2>
                        </div>
                        <button
                            onClick={() => navigate('/template5/shop')}
                            className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-[#2C2C2C] border-b border-[#2C2C2C]/30 pb-0.5 hover:border-[#2C2C2C] transition-colors self-start md:self-auto"
                            style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400 }}
                        >
                            View All <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                        {products.map((product) => (
                            <div key={product.id} className="group cursor-pointer" onClick={() => navigate(`/template5/product/${product.id}`)}>
                                <div className="relative overflow-hidden bg-[#F5F4F0] aspect-[3/4] mb-3 md:mb-4">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                                    />
                                    <div className="absolute inset-0 bg-[#1A1A1A]/0 group-hover:bg-[#1A1A1A]/10 transition-colors duration-500" />
                                    <button
                                        onClick={(e) => toggleWishlist(e, product.id)}
                                        className="absolute top-3 right-3 md:top-4 md:right-4 w-8 h-8 md:w-9 md:h-9 bg-[#FAF9F6] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white"
                                    >
                                        <Heart
                                            size={15}
                                            className={wishlist[product.id] ? 'text-red-500 fill-red-500' : 'text-[#2C2C2C]'}
                                        />
                                    </button>
                                </div>
                                <h3
                                    className="text-[#1A1A1A] text-sm md:text-base mb-0.5 group-hover:text-[#71717A] transition-colors duration-300"
                                    style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                                >
                                    {product.name}
                                </h3>
                                <p
                                    className="text-[10px] md:text-[11px] text-[#71717A] tracking-[0.1em] uppercase mb-1"
                                    style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}
                                >
                                    {product.category}
                                </p>
                                <span
                                    className="text-[#1A1A1A] text-sm"
                                    style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}
                                >
                                    {product.price}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 5: LOOKBOOK STRIP ===== */}
            <section className="py-14 md:py-20 lg:py-28" style={{ backgroundColor: '#F5F4F0' }}>
                <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
                    <div className="text-center mb-10 md:mb-14">
                        <p
                            className="text-[11px] md:text-xs tracking-[0.3em] uppercase text-[#71717A] mb-3"
                            style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}
                        >
                            Visual Stories
                        </p>
                        <h2
                            className="text-3xl md:text-4xl lg:text-5xl text-[#1A1A1A]"
                            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
                        >
                            The Lookbook
                        </h2>
                    </div>

                    {/* 3 Horizontal Images */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                        {lookbookImages.map((img, index) => (
                            <div key={index} className="relative overflow-hidden aspect-[4/5] group cursor-pointer">
                                <img
                                    src={img}
                                    alt={`Lookbook ${index + 1}`}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                                />
                                <div className="absolute inset-0 bg-[#1A1A1A]/0 group-hover:bg-[#1A1A1A]/15 transition-colors duration-500" />
                                <div className="absolute bottom-4 left-4 md:bottom-6 md:left-6">
                                    <span
                                        className="text-[10px] md:text-[11px] tracking-[0.2em] uppercase text-[#FAF9F6] bg-[#2C2C2C]/80 px-3 py-1.5"
                                        style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400 }}
                                    >
                                        Look {index + 1}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 6: BRAND STORY ===== */}
            <section className="py-14 md:py-20 lg:py-28" style={{ backgroundColor: '#FAF9F6' }}>
                <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center">
                        {/* Image */}
                        <div className="relative overflow-hidden aspect-[4/5] lg:aspect-[3/4]">
                            <img
                                src="https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=800"
                                alt="Our Story"
                                className="w-full h-full object-cover"
                            />
                        </div>

                        {/* Content */}
                        <div className="py-4 md:py-8">
                            <p
                                className="text-[11px] md:text-xs tracking-[0.3em] uppercase text-[#71717A] mb-4"
                                style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}
                            >
                                Our Story
                            </p>
                            <h2
                                className="text-3xl md:text-4xl lg:text-5xl text-[#1A1A1A] mb-6 leading-[1.1]"
                                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
                            >
                                Where Tradition Finds Its Modern Voice
                            </h2>
                            <div className="space-y-4 mb-8">
                                <p
                                    className="text-sm md:text-base text-[#71717A] leading-relaxed font-light"
                                    style={{ fontFamily: "'Outfit', sans-serif" }}
                                >
                                    Born from a love for Indian textiles and editorial design, TARA bridges the gap between heritage craftsmanship and contemporary fashion sensibility. Every piece in our collection tells a story that spans generations.
                                </p>
                                <p
                                    className="text-sm md:text-base text-[#71717A] leading-relaxed font-light"
                                    style={{ fontFamily: "'Outfit', sans-serif" }}
                                >
                                    We work with artisan communities across India, preserving techniques like Banarasi weaving, Zardozi embroidery, and Chanderi draping while reimagining them through a modern editorial lens.
                                </p>
                            </div>
                            <button
                                onClick={() => navigate('/template5/about')}
                                className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-[#2C2C2C] border-b border-[#2C2C2C]/30 pb-0.5 hover:border-[#2C2C2C] transition-colors"
                                style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400 }}
                            >
                                Read Our Story <ArrowRight className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 7: TESTIMONIALS ===== */}
            <section className="py-14 md:py-20 lg:py-28" style={{ backgroundColor: '#F5F4F0' }}>
                <div className="max-w-[900px] mx-auto px-4 md:px-6 lg:px-8 text-center">
                    <p
                        className="text-[11px] md:text-xs tracking-[0.3em] uppercase text-[#71717A] mb-3"
                        style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}
                    >
                        Voices
                    </p>
                    <h2
                        className="text-3xl md:text-4xl lg:text-5xl text-[#1A1A1A] mb-10 md:mb-14"
                        style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
                    >
                        What They Say
                    </h2>

                    <div
                        className="relative"
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                    >
                        <Quote className="w-8 h-8 md:w-10 md:h-10 text-[#2C2C2C]/10 mx-auto mb-6" />
                        <div className="min-h-[180px] md:min-h-[160px] flex items-center justify-center">
                            <div className="transition-opacity duration-500">
                                <p
                                    className="text-lg md:text-xl lg:text-2xl text-[#1A1A1A] leading-relaxed mb-6 italic"
                                    style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
                                >
                                    "{testimonials[activeTestimonial].text}"
                                </p>
                                <div className="flex items-center justify-center gap-1.5 mb-3">
                                    {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
                                        <Star key={i} size={14} className="text-[#2C2C2C] fill-[#2C2C2C]" />
                                    ))}
                                </div>
                                <p
                                    className="text-sm text-[#1A1A1A] tracking-wide"
                                    style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}
                                >
                                    {testimonials[activeTestimonial].name}
                                </p>
                                <p
                                    className="text-xs text-[#71717A] tracking-wide"
                                    style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}
                                >
                                    {testimonials[activeTestimonial].city}
                                </p>
                            </div>
                        </div>

                        {/* Dots */}
                        <div className="flex justify-center gap-2 mt-8">
                            {testimonials.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setActiveTestimonial(index)}
                                    className={`transition-all duration-300 ${index === activeTestimonial ? 'w-8 h-1 bg-[#2C2C2C]' : 'w-1 h-1 bg-[#2C2C2C]/30 rounded-full hover:bg-[#2C2C2C]/50'}`}
                                />
                            ))}
                        </div>

                        {/* Desktop Arrows */}
                        <button
                            onClick={() => setActiveTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length)}
                            className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center text-[#2C2C2C]/40 hover:text-[#2C2C2C] transition-colors"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => setActiveTestimonial((prev) => (prev + 1) % testimonials.length)}
                            className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center text-[#2C2C2C]/40 hover:text-[#2C2C2C] transition-colors"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 8: NEWSLETTER ===== */}
            <section className="py-16 md:py-24 lg:py-32" style={{ backgroundColor: '#2C2C2C' }}>
                <div className="max-w-[700px] mx-auto px-4 md:px-6 lg:px-8 text-center">
                    <p
                        className="text-[11px] md:text-xs tracking-[0.3em] uppercase text-[#FAF9F6]/50 mb-3"
                        style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}
                    >
                        Stay Inspired
                    </p>
                    <h2
                        className="text-3xl md:text-4xl lg:text-5xl text-[#FAF9F6] mb-4"
                        style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
                    >
                        Join The Edit
                    </h2>
                    <p
                        className="text-sm md:text-base text-[#FAF9F6]/50 mb-8 md:mb-10 font-light leading-relaxed"
                        style={{ fontFamily: "'Outfit', sans-serif" }}
                    >
                        Subscribe to receive editorial features, new collection previews, and exclusive invitations to private showings.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 max-w-[480px] mx-auto">
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Your email address"
                            className="flex-1 bg-transparent border-b border-[#FAF9F6]/30 px-1 py-3 text-sm text-[#FAF9F6] placeholder-[#FAF9F6]/30 outline-none focus:border-[#FAF9F6] transition-colors"
                            style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}
                        />
                        <button
                            className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-[#FAF9F6]/30 text-[#FAF9F6] text-xs tracking-[0.15em] uppercase hover:bg-[#FAF9F6] hover:text-[#2C2C2C] transition-all duration-300"
                            style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400 }}
                        >
                            Subscribe <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Template5;
